<template>
  <div :class="['content mb-5', { 'policy-no-subtitle': !pageMeta.subTitle }]">
    <t-sub-nav
      class="mb-5"
      :loading="loading"
      :items="
        $secondaryNav.customerDefaultPolicyNavItems(
          hasVirtualLocation(selectedCustomerPolicy)
        )
      "
    />

    <h2 class="mb-4">{{ this.$t("Filtering Options") }}</h2>
    <h3 class="mb-3">{{ this.$t("Safe Search") }}</h3>
    <p class="mb-3">
      {{
        this.$t(
          "Safe Search filters out explicit or potentially offensive search results and helps avoid most adult content search results"
        )
      }}
    </p>
    <b-row class="mb-5">
      <b-col sm="12" md="6" lg="6">
        <t-checkbox
          white-bg
          data-test-id="global-default-policy-safe-search"
          v-if="!loading"
          :label="this.$t('Safe Search')"
          :value-true="this.$t('ON')"
          :value-false="this.$t('OFF')"
          v-model="$v.form.safesearch.$model"
          id="safe_search"
        />
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>
    <h3 class="mb-3">{{ this.$t("Unclassified Traffic") }}</h3>
    <p class="mb-3">
      {{
        this.$t(
          "Unclassified traffic is from websites that are new and are not yet assigned to a category in the central web categories database"
        )
      }}
    </p>
    <b-row class="mb-6">
      <b-col sm="12" md="6" lg="6">
        <t-checkbox
          white-bg
          data-test-id="default-policy-unclassified-traffic"
          v-if="!loading"
          :label="this.$t('Allow Unclassified Traffic')"
          :value-true="true"
          :value-false="false"
          v-model="$v.form.allowunclassified.$model"
          id="unclassified_traffic"
        />
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>

    <h2 class="mb-3">{{ this.$t("Content Filtering") }}</h2>
    <p class="mb-3">
      {{
        this.$t("Choose a level of filtering to apply to your default policy.")
      }}
    </p>
    <b-row class="mb-4">
      <b-col sm="12">
        <t-radio-group
          v-model="form.level"
          :loading="loading"
          :items="contentFiltering"
          @change="canShowCustomise(form.level)"
        />
      </b-col>
    </b-row>

    <t-save-button
      data-test-id="default-policy-unclassified-save"
      :form-model="$v.form"
      :fetching="fetching"
      @submit="onSubmit"
    />
  </div>
</template>

<route>
{
  "name": "Customer Default Policy",
  "meta": {
    "authorize" : [1, 3, 4]
  }
}
</route>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import policySubPages from "@/mixins/policySubPages";

export default {
  layout: "customer",
  mixins: [policySubPages],

  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("customerPolicies", [
      "fetchCustomerPolicy",
      "updateCustomerPolicy",
    ]),
    ...mapActions("customerDomains", [
      "fetchCustomerDomainList",
      "deleteCustomerDomainListEntrys",
      "addBlockAllowedUser",
    ]),
    ...mapActions("categories", ["fetchPolicyCategories"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("fileUpload", ["setDialog", "clearCache"]),

    ...mapMutations("modal", ["createModal", "showModal"]),
    canShowCustomise(val) {
      this.$v.$touch();
      if (val === "C") {
        this.customise(val);
      }
    },
    customise(level) {
      if (level === "Custom" || level === "C") {
        this.createModal({
          modal: "t-categories",
          dataTestId: "modal-categories",
          title: this.$t("Custom Content Filter"),
          subtitle: this.$t("Level") + ": " + this.$t(level),
          modalData: {
            policy: this.selectedCustomerPolicy,
            canSelectAlL: true,
          },
          props: {
            size: "xl",
            okTitle: this.$t("Save Changes"),
          },
        });

        this.showModal();
      }
    },
    getCategories(level) {
      switch (level) {
        case "Essential":
          return this.lowLevelCategories;
        case "Enhanced":
          return this.moderateLevelCategories;
        case "Optimum":
          return this.highLevelCategories;
        default:
          return this.customerCategories;
      }
    },

    async onSubmit() {
      this.fetching = true;
      try {
        await this.updateCustomerPolicy({
          id: this.selectedUser.id,
          policyId: this.selectedCustomerPolicy.id,
          policy: {
            safesearch: this.form.safesearch,
            allowunclassified: this.form.allowunclassified,
            level: this.form.level,
          },
        });
        this.displayToast({
          title: this.$t("Success"),
          message: "Updated",
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching = false;
        this.$v.form.$reset();
      }
    },
  },
  computed: {
    ...mapGetters("authentication", ["selectedUser"]),
    ...mapGetters("customerPolicies", ["selectedCustomerPolicy"]),
    ...mapGetters("pageMeta", ["pageMeta"]),
    ...mapGetters("categories", ["customerCategories"]),
    ...mapGetters("categories", [
      "lowLevelCategories",
      "moderateLevelCategories",
      "highLevelCategories",
    ]),
    ...mapGetters("fileUpload", ["modal"]),
  },
  data: function () {
    return {
      loading: true,
      fetching: false,
      form: {
        safesearch: null,
        allowunclassified: null,
        emailnotofications: null,
        level: "M",
      },

      contentFiltering: [
        {
          text: this.$t("Essential"),
          subtitle: this.$t("Essential security protection."),
          tooltip: this.$t(
            "Blocks these categories: Anonymizer, Compromised, Criminal Skills/Hacking, Hate Speech, Phishing/Fraud, Spam, Spyware and Malicious Sites."
          ),
          testID: "default-policy-content-filtering-low",
          value: "L",
          checked: false,
          icon: "shield",
        },
        {
          text: this.$t("Enhanced"),
          subtitle: this.$t(
            "Enhanced security protection, plus the blocking of adult related content."
          ),
          tooltip: this.$t(
            "Blocks Essential categories, plus Download Sites, Illegal Drugs, Online Ads, Pornography/Sex, Violence."
          ),
          testID: "default-policy-content-filtering-moderate",
          value: "M",
          checked: true,
          icon: "shield-shaded",
        },
        {
          text: this.$t("Optimum"),
          subtitle: this.$t(
            "Your own custom protection level to allow or block access to selected categories by default."
          ),
          tooltip: this.$t(
            "Blocks Essential and Enhanced categories, plus Alcohol, Dating, Gambling, Nudity, Weapons."
          ),
          testID: "default-policy-content-filtering-high",
          value: "H",
          checked: false,
          icon: "shield-fill",
        },
        {
          text: this.$t("Custom"),
          subtitle: this.$t(
            "Enhanced security protection, blocking of adult related content, plus blocking of productivity inhibiting sites."
          ),
          tooltip: "",
          testID: "default-policy-content-filtering-Custom",
          value: "C",
          checked: false,
          icon: "shield-fill-plus",
          canCustomise: true,
          customiseFunction: () => this.customise("Custom"),
        },
      ],
    };
  },
  validations: {
    form: {
      safesearch: {},
      emailnotofications: {},
      allowunclassified: {},
      level: {},
    },
  },
  watch: {
    selectedCustomerPolicy() {
      this.form = this.selectedCustomerPolicy;
    },
  },

  async created() {
    await this.fetchCustomerPolicy({
      id: this.selectedUser.id,
      policyId: "default",
    });
    try {
      await Promise.all([
        this.fetchPolicyCategories({
          id: this.selectedUser.id,
          policyId: this.selectedCustomerPolicy.id,
          tier: "user_id",
        }),
        this.fetchCustomerDomainList({
          id: this.selectedUser.id,
          policyId: "default",
        }),
      ]);
    } finally {
      this.loading = false;
    }
  },
  mount() {
    this.setPageTitle(this.$t("Default Policy"));
    this.setPageSubtitle([
      this.$t(
        "Policies determine what categories of website a user can see. This is your default policy and is automatically assigned to all new locations, devices, users and groups added to WebTitan, unless you apply a policy you have created in Content Filtering > Policies"
      ),
    ]);
  },
};
</script>
